/**
 * Gift ladder — free gifts driven by the subscription plan in the cart.
 *
 * Each gift declares which plans it comes with, mirroring the pricing table
 * row by row. There is deliberately no tier inheritance: a gift is granted if
 * and only if one of its own plans is in the cart. The table already says
 * which gifts belong to which plans, so expressing it directly beats making
 * anyone reason about what a tier number inherits.
 *
 * A second kind of gift, "Gift for a quantity", is granted by how many of one
 * variant the cart holds instead — the Good Mood 8 Pack, which is two Variety
 * 4 Packs. It pairs with a Shopify Buy X Get Y discount that makes it free;
 * see quantity gifts below.
 *
 * Owns no cart machinery of its own. It subscribes to the cart layer in
 * assets/pick-a-card-cart.js and asks it to reconcile gift lines, so there is
 * one mutation lock, one dedupe and one guard across both gift features. Two
 * systems independently adding and removing $0.00 lines would race.
 *
 * Free shipping is NOT handled here — that is a Shopify shipping rule, set in
 * Settings > Shipping, and applies at checkout without a cart line.
 */
(function () {
  'use strict';

  if (window.THHGiftLadder) return;

  var SOURCE = 'Ladder';

  var config = null;
  var started = false;
  var lastSignature = '';

  function log(message, error) {
    if (window.console && console.warn) console.warn('[gift-ladder] ' + message, error || '');
  }

  function readConfig() {
    var node = document.querySelector('[data-gift-ladder-config]');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (e) {
      log('bad config JSON', e);
      return null;
    }
  }

  /** The Skio plan name on a cart line, lowercased, or '' for a one-time buy. */
  function planNameOf(item) {
    var allocation = item && item.selling_plan_allocation;
    var plan = allocation && allocation.selling_plan;
    return plan && plan.name ? String(plan.name).toLowerCase() : '';
  }

  /**
   * Every configured plan fragment present in the cart.
   *
   * Matched on a name fragment rather than a plan id: ids change when a plan
   * is recreated and the failure would be silent, gifts simply ceasing to
   * appear. A fragment also survives Shopify appending the delivery interval
   * to the name, e.g. "30 Nights - Every month".
   */
  function plansInCart(cart) {
    var found = [];
    if (!cart || !Array.isArray(cart.items)) return found;

    var names = cart.items.map(planNameOf).filter(Boolean);
    (config.gifts || []).forEach(function (gift) {
      (gift.plans || []).forEach(function (fragment) {
        var needle = String(fragment || '').trim().toLowerCase();
        if (!needle) return;
        if (found.indexOf(needle) !== -1) return;
        var present = names.some(function (name) { return name.indexOf(needle) !== -1; });
        if (present) found.push(needle);
      });
    });
    return found;
  }

  /**
   * Gifts whose plan list intersects what is in the cart, minus any the
   * shopper already has from another source.
   *
   * Supersession is applied here rather than left to the cart layer's own
   * sweep. Asking for a gift that is about to be withdrawn would have this
   * add it, the sweep remove it, the change signature move, and the whole
   * thing start again — the cart layer's sweep stays as a backstop for gifts
   * granted before a rule existed, not as the thing that makes this correct.
   */
  function giftsForCart(cart) {
    var active = plansInCart(cart);
    var blocked = supersedingPresent(cart);
    var ids = [];
    (config.gifts || []).forEach(function (gift) {
      var earned = gift.minQuantity
        ? quantityEarned(gift, cart)
        : (gift.plans || []).some(function (fragment) {
          return active.indexOf(String(fragment || '').trim().toLowerCase()) !== -1;
        });
      if (!earned) return;
      if (gift.supersededBy && blocked.indexOf(String(gift.supersededBy)) !== -1) return;
      if (ids.indexOf(gift.variantId) === -1) ids.push(gift.variantId);
    });
    return ids;
  }

  // ---- quantity gifts ----

  /**
   * Gifts withdrawn because the cart priced them, keyed by the gift's rule —
   * its position in the config — each holding the qualifying quantity at the
   * time. Not by gift variant: two rules can grant the same gift (the Variety
   * Pack for 2 x Variety 4 Pack, and for any 8 Pack), and one rule's count
   * overwriting the other's would re-add the charged line in a loop.
   *
   * A quantity gift is a full-price product made free by a Shopify Buy X Get Y
   * discount, so if that discount is ever paused or edited the line would sit
   * in the cart at full price under a FREE badge. Withdrawing it is the fix;
   * remembering the quantity stops the next signal re-adding it straight
   * away. A different quantity is a different cart, so it gets one retry.
   */
  var refused = {};

  function ruleKey(gift) {
    return String((config.gifts || []).indexOf(gift));
  }

  /**
   * Units of a gift's qualifying variants the shopper is buying, added up
   * across them and ignoring gift lines — "buy N of these", the way the
   * discount counts.
   */
  function qualifyingQuantity(cart, gift) {
    var ids = (gift.qualifyingVariantIds || []).map(String);
    return cart.items.reduce(function (sum, item) {
      if (ids.indexOf(String(item.variant_id)) === -1) return sum;
      if (item.properties && (item.properties.Gift || item.properties._auto_gift)) return sum;
      return sum + (Number(item.quantity) || 0);
    }, 0);
  }

  /**
   * The Good Mood PDP used to add this same gift itself, tagged _auto_gift.
   * A cart from before that moved here can still hold one, and a second copy
   * from us would be one more than the discount frees.
   */
  function legacyCopyPresent(cart, variantId) {
    var id = String(variantId);
    return cart.items.some(function (item) {
      return String(item.variant_id) === id && !!(item.properties && item.properties._auto_gift);
    });
  }

  function quantityEarned(gift, cart) {
    var quantity = qualifyingQuantity(cart, gift);
    if (quantity < gift.minQuantity) return false;
    if (refused[ruleKey(gift)] === quantity) return false;
    return !legacyCopyPresent(cart, gift.variantId);
  }

  function addedQuantityGift(addedIds) {
    return (config.gifts || []).some(function (gift) {
      return gift.minQuantity && (addedIds || []).map(String).indexOf(String(gift.variantId)) !== -1;
    });
  }

  /** Record any quantity gift of ours that the cart is charging for. */
  function noteChargedGifts(cart) {
    (config.gifts || []).forEach(function (gift) {
      if (!gift.minQuantity) return;
      var id = String(gift.variantId);
      var charged = cart.items.some(function (item) {
        return String(item.variant_id) === id &&
          item.properties && item.properties.Gift === SOURCE &&
          item.final_line_price > 0;
      });
      if (!charged) return;
      refused[ruleKey(gift)] = qualifyingQuantity(cart, gift);
      log('"' + gift.label + '" is not free in this cart — is its discount active? Withdrawing it.');
    });
  }

  /**
   * Which superseding products are currently in the cart, added by something
   * other than us.
   *
   * Part of the change signature below, not just a filter. Supersession can
   * flip without the earned-gift list changing at all — Rebuy's threshold is
   * crossed, or stops being met — and if the signature ignored that, the
   * reconcile would be skipped and the gift would never come back when their
   * line went away.
   */
  function supersedingPresent(cart) {
    var present = [];
    if (!cart || !Array.isArray(cart.items)) return present;
    (config.gifts || []).forEach(function (gift) {
      if (!gift.supersededBy) return;
      // A gift set to replace the other app's copy is never withheld by it:
      // the cart layer withdraws their line, and withholding ours in the
      // meantime would leave the shopper with neither.
      if (gift.replaceInstead) return;
      var id = String(gift.supersededBy);
      if (present.indexOf(id) !== -1) return;
      var found = cart.items.some(function (item) {
        if (String(item.variant_id) !== id) return false;
        // A line of ours cannot supersede our own gift.
        return !(item.properties && item.properties.Gift);
      });
      if (found) present.push(id);
    });
    return present.sort();
  }

  /**
   * The gift lines actually in the cart, each tagged with its grantor.
   *
   * Part of the change signature so the memo tracks the cart rather than our
   * intent. Without it, a gift line removed by the guard — or one carrying
   * the wrong grantor, as ladder gifts did before the cart layer honoured the
   * source argument — leaves the earned list unchanged, the reconcile skipped,
   * and the gift missing until the next page load.
   */
  function giftLinesPresent(cart) {
    var tokens = [];
    if (!cart || !Array.isArray(cart.items)) return tokens;
    cart.items.forEach(function (item) {
      var source = item.properties && item.properties.Gift;
      if (!source) return;
      tokens.push(String(item.variant_id) + ':' + source);
    });
    return tokens.sort();
  }

  function apply(cart) {
    noteChargedGifts(cart);
    var wanted = giftsForCart(cart);

    // Skip the round trip when nothing changed. The cart layer fires on every
    // cart signal, including ones this feature caused.
    var signature = wanted.slice().sort().join(',') +
      '|' + supersedingPresent(cart).join(',') +
      '|' + giftLinesPresent(cart).join(',');
    if (signature === lastSignature) return;
    lastSignature = signature;

    window.THHPickACard.reconcileGifts(SOURCE, wanted).then(function (outcome) {
      if (outcome && (outcome.added.length || outcome.removed.length)) track(wanted, outcome);
      // Our own writes raise no cart signal, so nothing else would look at the
      // price a just-added quantity gift landed at. Look once now; a charged
      // line is withdrawn by the pass this triggers.
      if (outcome && addedQuantityGift(outcome.added)) {
        window.THHPickACard.readCart().then(function (next) {
          if (next) apply(next);
        });
      }
    }).catch(function (e) {
      // Let the next cart signal retry rather than wedging on a stale
      // signature after a failure.
      lastSignature = '';
      log('reconcile failed', e);
    });
  }

  function track(wanted, outcome) {
    var payload = {
      ladder_gifts: wanted.length,
      ladder_added: outcome.added.length,
      ladder_removed: outcome.removed.length
    };
    try { if (window.clarity) window.clarity('set', 'gift_ladder_gifts', String(wanted.length)); } catch (e) {}
    try {
      if (typeof window.gtag === 'function') window.gtag('event', 'gift_ladder_change', payload);
    } catch (e) {}
    try {
      if (window.dataLayer && typeof window.dataLayer.push === 'function') {
        window.dataLayer.push({
          event: 'gift_ladder_change',
          ladder_gifts: payload.ladder_gifts
        });
      }
    } catch (e) {}
  }

  function start() {
    if (started) return;
    config = readConfig();
    if (!config || !config.enabled) return;
    if (!config.gifts || !config.gifts.length) return;

    // Hard dependency: without the cart layer there is no lock, no dedupe and
    // no guard. Adding gifts anyway would create exactly the unguarded $0.00
    // lines the guard exists to prevent.
    if (!window.THHPickACard || typeof window.THHPickACard.reconcileGifts !== 'function') {
      log('cart layer not present; ladder disabled');
      return;
    }

    started = true;

    // Registered from every configured gift, not just the earned ones. A gift
    // granted on an earlier visit is still in the cart, and its rule has to be
    // loaded now so the cart layer can withdraw it if another app has since
    // added the real product.
    if (typeof window.THHPickACard.registerSupersessions === 'function') {
      window.THHPickACard.registerSupersessions(
        (config.gifts || [])
          .filter(function (g) { return g.variantId && g.supersededBy && !g.replaceInstead; })
          .map(function (g) { return { gift: g.variantId, supersededBy: g.supersededBy }; })
      );
    }

    // Registered from every configured gift for the same reason supersessions
    // are: the other app's line can already be in a cart from an earlier
    // visit, and its rule has to be loaded now for the cart layer to withdraw
    // it — not only when this plan happens to earn the gift.
    if (typeof window.THHPickACard.registerReplacements === 'function') {
      window.THHPickACard.registerReplacements(
        (config.gifts || [])
          .filter(function (g) { return g.variantId && g.supersededBy && g.replaceInstead; })
          .map(function (g) {
            return {
              replace: g.supersededBy,
              // Only make room when this cart earns our copy. Otherwise their
              // gift is the only one the shopper would get.
              when: function (cart) { return giftsForCart(cart).indexOf(g.variantId) !== -1; }
            };
          })
      );
    }

    window.THHPickACard.onCartChange(apply);
    window.THHPickACard.readCart().then(function (cart) {
      if (cart) apply(cart);
    });
  }

  window.THHGiftLadder = {
    version: 2,
    giftsForCart: giftsForCart,
    config: function () { return config; },

    /**
     * Setup aid. The plan-name fragments are the one thing that cannot be
     * checked from the theme editor — a wrong fragment simply means the gift
     * is never granted, silently. This prints what the cart actually reports
     * against what each gift is waiting for.
     */
    plansInCart: function () {
      return window.THHPickACard.readCart().then(function (cart) {
        if (!cart) { log('could not read the cart'); return []; }
        var rows = cart.items.map(function (item) {
          return {
            line: item.product_title,
            planName: planNameOf(item) || '(one-time purchase)'
          };
        });
        var earned = giftsForCart(cart);
        (config && config.gifts ? config.gifts : []).forEach(function (gift) {
          var waitingFor = gift.minQuantity
            ? gift.minQuantity + ' of variants ' + (gift.qualifyingVariantIds || []).join(', ') +
              ' (cart has ' + qualifyingQuantity(cart, gift) + ')'
            : (gift.plans || []).join(' or ');
          rows.push({
            line: 'GIFT: ' + gift.label,
            planName: 'waiting for: ' + waitingFor +
              ' — ' + (earned.indexOf(gift.variantId) !== -1 ? 'GRANTED' : 'not granted')
          });
        });
        if (window.console && console.table) console.table(rows);
        return rows;
      });
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
